import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ColumnState } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { PDA1131_R01Dto } from "./PDA1131_R01Dto";
import { aggridHeaderClass, pdaCellStyle } from "src/components/common/common";

/** 목록 그리드 props */
interface PDA1131_R01TableProps {
  shippingOrderList: PDA1131_R01Dto[];
  onSelectOrder: (order: PDA1131_R01Dto) => void;
}

/** GridContextWrapper 호환용 inserted 플래그 */
type ShippingOrderWithInserted = PDA1131_R01Dto & { inserted?: boolean };

/** 출하지시 목록 AG Grid — 행 클릭 시 상세 진입 */
const PDA1131_R01Table = ({ shippingOrderList, onSelectOrder }: PDA1131_R01TableProps) => {
  /** AG Grid ref */
  const gridRef = useRef<AgGridReact<ShippingOrderWithInserted>>(null);
  /** GridContextWrapper 래퍼 ref */
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  /** 행 hover 인덱스 (Grid_Select 필수 prop) */
  const [, setHoveredRowIndex] = useState<number | null>(null);
  /** 컬럼 상태 (localStorage 연동) */
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  /** 그리드 rowData — inserted: false 고정 */
  const paddedGridData = useMemo(() => {
    return shippingOrderList.map((order) => ({ ...order, inserted: false }));
  }, [shippingOrderList]);

  /** 목록 컬럼 정의 */
  const columnDefs: ColDef<ShippingOrderWithInserted>[] = useMemo(
    () => [
      {
        headerName: "일자",
        field: "orderDate",
        valueGetter: (params) => params.data?.orderDate || "-",
        cellStyle: { textAlign: "center", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 0.7,
        minWidth: 80,
      },
      {
        headerName: "거래처",
        field: "customCd",
        valueGetter: (params) => params.data?.customCd || "-",
        cellStyle: { textAlign: "center", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 0.8,
        minWidth: 80,
      },
      {
        headerName: "상호",
        field: "custNm",
        valueGetter: (params) => params.data?.custNm || "-",
        cellStyle: { textAlign: "left", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 80,
      },
      {
        headerName: "출고품목",
        field: "shippingItems",
        valueGetter: (params) => params.data?.shippingItems || "-",
        cellStyle: { textAlign: "left", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 80,
      },
    ],
    []
  );

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={()=>{}}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1131_R01_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<ShippingOrderWithInserted>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            rowSelection="single"
            onRowClicked={(event) => {
              if (event.data) {
                onSelectOrder(event.data);
              }
            }}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1131_R01_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1131_R01Table;
